import { Link, NavLink } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { queryClient } from 'config/query-client';
import { useState } from 'react';

import useTranslation from 'customHooks/translate';
import { useDialogHandler } from 'hooks/useDialogHandler';
import AddNewJobNumberForm from 'pages/Dialogs/AddNewJobNumberForm';
import AddNewManagerForm from 'pages/Dialogs/AddNewManagerForm';
import AddNewPartnerForm from 'pages/Dialogs/AddNewPartnerForm';
import { BALANCES } from 'events/types';

import { sidebarAddLinks, sidebarLinks } from './menu';

import './index.css';

interface IProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isSidebarCollapsed: boolean) => void;
}

const SidebarNav = ({ isSidebarOpen, setIsSidebarOpen }: IProps) => {
  const translation = useTranslation();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [managerDialogState, openManagerDialog, closeManagerDialog] =
    useDialogHandler();
  const [jobDialogState, openJobDialog, closeJobDialog] = useDialogHandler();
  const [debitorDialogState, openDebitorDialog, closeDebitorDialog] =
    useDialogHandler();
  const [creditorDialogState, openCreditorrDialog, closeCreditorDialog] =
    useDialogHandler();

  const buttonMapper: Record<string, () => void> = {
    [`${translation.sideMenu.Manager}`]: openManagerDialog,
    [`${translation.jobNumbers.Header}`]: openJobDialog,
    [`${translation.dashboard.PartnerButtonCr}`]: openCreditorrDialog,
    [`${translation.dashboard.PartnerButtonDr}`]: openDebitorDialog,
  };

  const links = sidebarLinks(translation);
  const extraLinks = sidebarAddLinks(translation);

  return (
    <>
      <nav className={`sidebar-nav ${isSidebarOpen ? 'open' : 'collapsed'}`}>
        <div
          className={`mb-[45px] flex ${
            isSidebarOpen ? 'justify-between' : 'justify-center'
          } `}
        >
          {isSidebarOpen && (
            <Link to="/dashboard">
              <img
                className="ml-[16px] h-[30px] w-[90px]"
                src="assets/logo.png"
                alt="logo"
              />
            </Link>
          )}
          <div
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[10px] border border-[rgba(70,106,251,0.12)]"
          >
            {isSidebarOpen ? (
              <ArrowBackIosNewIcon
                style={{ color: '#466AFB', width: '14px', height: '14px' }}
              />
            ) : (
              <ArrowForwardIosIcon
                style={{ color: '#466AFB', width: '14px', height: '14px' }}
              />
            )}{' '}
          </div>
        </div>
        {links?.map((link) => {
          const hasChildren = Array.isArray(link.children);

          if (!hasChildren) {
            return (
              <NavLink
                key={link.name}
                to={link.url}
                className={({ isActive }) =>
                  `menu-link ${isActive ? 'active' : ''} ${
                    isSidebarOpen ? '' : 'justify-center'
                  }`
                }
              >
                <img
                  className="menu-link-image"
                  src={`assets/${link.icon}.svg`}
                  alt=""
                />
                <span>{link.name}</span>
              </NavLink>
            );
          }

          const isOpen = openDropdown === link.name;

          return (
            <div key={link.name}>
              <div
                className={`menu-link ${
                  isSidebarOpen ? 'justify-between' : 'justify-center'
                }`}
                onClick={() => setOpenDropdown(isOpen ? null : link.name)}
                style={{ cursor: 'pointer' }}
              >
                <div className="flex items-center gap-[8px]">
                  <img
                    className="menu-link-image"
                    src={`assets/${link.icon}.svg`}
                    alt=""
                  />
                  <span>{link.name}</span>
                </div>
                {isSidebarOpen && (
                  <img
                    className="h-[15px] w-[15px]"
                    src={`assets/arrow-up-side.svg`}
                    alt=""
                  />
                )}
              </div>

              {isOpen && (
                <div
                  className={`${
                    isSidebarOpen ? 'ml-[20px]' : ''
                  }  flex flex-col`}
                >
                  {link?.children?.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.url}
                      className={({ isActive }) =>
                        `menu-link child-link ${isActive ? 'active' : ''} ${
                          isSidebarOpen ? '' : 'justify-center'
                        }`
                      }
                    >
                      <img
                        className="menu-link-image"
                        src={`assets/${child.icon}.svg`}
                        alt=""
                      />
                      <span>{child.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="sidebar-nav-line" />
        {extraLinks?.map((link) => (
          <NavLink
            key={link.name}
            to={link.url || '#'}
            className={({ isActive }) =>
              `menu-link ${isActive && !link?.modalLink ? 'active' : ''} ${
                isSidebarOpen ? `` : 'justify-center'
              }`
            }
            onClick={buttonMapper[link.name] || undefined}
          >
            <img
              className="menu-link-image"
              src={`assets/${link.icon}.svg`}
              alt=""
            />
            <span>{link.name}</span>
          </NavLink>
        ))}
      </nav>
      <AddNewManagerForm
        open={managerDialogState.isOpen}
        handleClose={closeManagerDialog}
      />
      <AddNewJobNumberForm
        open={jobDialogState.isOpen}
        handleClose={closeJobDialog}
      />
      <AddNewPartnerForm
        open={debitorDialogState.isOpen}
        handleClose={closeDebitorDialog}
        onPartnerChange={() => {
          queryClient.invalidateQueries({
            queryKey: [BALANCES],
            refetchType: 'all',
          });
        }}
        isCreditor={false}
      />
      <AddNewPartnerForm
        open={creditorDialogState.isOpen}
        handleClose={closeCreditorDialog}
        onPartnerChange={() => {
          queryClient.invalidateQueries({
            queryKey: [BALANCES],
            refetchType: 'all',
          });
        }}
        isCreditor={true}
      />
    </>
  );
};

export default SidebarNav;
